import type { FC, ReactNode } from 'react'
import type { LayoutProps } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { LogOut, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { Avatar } from '@/components/ui/feedback'
import { BrandMark } from '@/components/app/brand-mark'
import { LiveClock } from '@/components/app/live-clock'
import { ThemeToggle } from '@/components/app/theme-toggle'
import { YearSwitcher } from '@/components/app/year-switcher'
import { useAcademic } from '@/components/providers/academic-provider'
import { NAV_GROUPS, PAGE_TITLES } from '@/constants/navigation'
import { ROLE_LABELS } from '@/constants/academic'

type SidebarContentProps = {
    pathname: string
    mobile?: boolean
    onNavigate?: () => void
}

const SidebarContent: FC<SidebarContentProps> = ({ pathname, mobile = false, onNavigate }): ReactNode => {
    const { data, currentUser } = useAcademic()
    const { settings } = data
    const isAdmin = currentUser.role === 'admin'

    return (
        <div className='flex h-full min-h-0 flex-col'>
            <div className='flex min-h-16 shrink-0 items-center px-5 py-3.5'>
                <Link
                    href='/'
                    onClick={onNavigate}
                    className='flex min-w-0 items-center gap-3 rounded-lg outline-none focus-visible:ring-3 focus-visible:ring-ring/40'
                >
                    {settings.showLogo && <BrandMark />}

                    <span className='min-w-0'>
                        <span className='block truncate text-[15px] font-bold tracking-tight text-foreground'>
                            {settings.appName}
                        </span>

                        {settings.tagline && (
                            <span className='block truncate text-xs text-muted-foreground'>
                                {settings.tagline}
                            </span>
                        )}
                    </span>
                </Link>
            </div>

            {mobile && (
                <div className='px-4 pb-2'>
                    <YearSwitcher id='tahun-ajaran-mobile' />
                </div>
            )}

            <nav aria-label='Navigasi utama' className='min-h-0 flex-1 overflow-y-auto px-3 pb-4'>
                {NAV_GROUPS.map(group => {
                    const items = group.items.filter(item => !item.adminOnly || isAdmin)

                    if (items.length === 0) return null

                    return (
                        <div key={group.label} className='mt-5 first:mt-2'>
                            <p className='px-3 pb-2 text-[11px] font-semibold tracking-[0.12em] text-muted-foreground/85 uppercase'>
                                {group.label}
                            </p>

                            <ul className='space-y-0.5'>
                                {items.map(item => {
                                    const active = item.match.includes(pathname)
                                    const Icon = item.icon

                                    return (
                                        <li key={item.href}>
                                            <Link
                                                href={item.href}
                                                onClick={onNavigate}
                                                aria-current={active ? 'page' : undefined}
                                                className={cn(
                                                    'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/40',
                                                    active
                                                        ? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground'
                                                        : 'font-medium text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-foreground'
                                                )}
                                            >
                                                {active && (
                                                    <span className='absolute top-1/2 -left-3 h-5 w-1 -translate-y-1/2 rounded-r-full bg-sidebar-primary' aria-hidden='true' />
                                                )}

                                                <Icon
                                                    className={cn(
                                                        'size-[18px] shrink-0 transition-colors',
                                                        active ? 'text-sidebar-primary' : 'text-muted-foreground group-hover:text-foreground'
                                                    )}
                                                    aria-hidden='true'
                                                />

                                                <span className='truncate'>
                                                    {item.label}
                                                </span>
                                            </Link>
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    )
                })}
            </nav>

            <div className='shrink-0 border-t border-sidebar-border p-3'>
                <div className='flex items-center gap-3 rounded-xl bg-muted/60 p-2.5'>
                    <Avatar name={currentUser.name} />

                    <div className='min-w-0 flex-1'>
                        <p className='truncate text-[13px] font-semibold text-foreground'>
                            {currentUser.name}
                        </p>

                        <p className='truncate text-xs text-muted-foreground'>
                            {ROLE_LABELS[currentUser.role]} · @{currentUser.username}
                        </p>
                    </div>

                    <Link
                        href='/login'
                        onClick={onNavigate}
                        className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
                        aria-label='Keluar dari akun'
                        title='Keluar'
                    >
                        <LogOut aria-hidden='true' />
                    </Link>
                </div>
            </div>
        </div>
    )
}

export const Layout: FC<LayoutProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const { data } = useAcademic()
    const [menuOpen, setMenuOpen] = useState(false)

    const title = PAGE_TITLES[router.pathname] ?? 'Portal Akademik'

    useEffect(() => {
        setMenuOpen(false)
    }, [router.asPath])

    useEffect(() => {
        if (!menuOpen) return

        const handleKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        document.addEventListener('keydown', handleKey)

        return () => document.removeEventListener('keydown', handleKey)
    }, [menuOpen])

    return (
        <div className='min-h-screen bg-background'>
            <a
                href='#konten'
                className='sr-only rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[70]'
            >
                Lewati ke konten
            </a>

            <aside className='fixed inset-y-0 left-0 z-40 hidden w-68 border-r border-sidebar-border bg-sidebar lg:block print:hidden'>
                <SidebarContent pathname={router.pathname} />
            </aside>

            <div className={cn('fixed inset-0 z-50 lg:hidden print:hidden', !menuOpen && 'pointer-events-none')}>
                <div
                    className={cn('absolute inset-0 bg-slate-950/50 backdrop-blur-[2px] transition-opacity duration-300', menuOpen ? 'opacity-100' : 'opacity-0')}
                    onClick={() => setMenuOpen(false)}
                    aria-hidden='true'
                />

                <aside
                    id='menu-samping'
                    inert={!menuOpen}
                    aria-label='Menu'
                    className={cn(
                        'absolute inset-y-0 left-0 flex w-[min(19rem,86vw)] flex-col border-r border-sidebar-border bg-sidebar shadow-float transition-transform duration-300 ease-out',
                        menuOpen ? 'translate-x-0' : '-translate-x-full'
                    )}
                >
                    <button
                        type='button'
                        onClick={() => setMenuOpen(false)}
                        className={buttonVariants({ variant: 'ghost', size: 'icon-sm', className: 'absolute top-4 right-3 z-10' })}
                        aria-label='Tutup menu'
                    >
                        <X aria-hidden='true' />
                    </button>

                    <SidebarContent pathname={router.pathname} mobile onNavigate={() => setMenuOpen(false)} />
                </aside>
            </div>

            <div className='lg:pl-68 print:pl-0'>
                <header className='sticky top-0 z-30 border-b bg-background/80 backdrop-blur-xl print:hidden'>
                    <div className='mx-auto flex h-16 max-w-[1440px] items-center gap-3 px-4 sm:px-6 lg:px-8'>
                        <button
                            type='button'
                            onClick={() => setMenuOpen(true)}
                            className={buttonVariants({ variant: 'outline', size: 'icon', className: 'lg:hidden' })}
                            aria-label='Buka menu'
                            aria-expanded={menuOpen}
                            aria-controls='menu-samping'
                        >
                            <Menu aria-hidden='true' />
                        </button>

                        <div className='min-w-0 flex-1'>
                            <p className='truncate text-[11px] font-semibold tracking-[0.12em] text-muted-foreground uppercase'>
                                {data.settings.tagline || data.settings.appName}
                            </p>

                            <p className='truncate text-[15px] leading-tight font-semibold text-foreground'>
                                {title}
                            </p>
                        </div>

                        <YearSwitcher id='tahun-ajaran-atas' className='hidden w-64 md:block' />

                        <div className='hidden h-8 w-px bg-border xl:block' aria-hidden='true' />

                        <LiveClock className='hidden xl:flex' />

                        <ThemeToggle />
                    </div>
                </header>

                <main id='konten' className='mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8 print:max-w-none print:p-0'>
                    {children}
                </main>
            </div>
        </div>
    )
}
